import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PLANS, TRIAL, ADDONS, ENTERPRISE, DEMO_BOOKING_URL } from "../../lib/pricing";
import PageTitle from "../../components/PageTitle";
import { usePageTitle } from "../../lib/usePageTitle";

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-left text-white font-semibold text-sm hover:bg-white/5 transition-colors"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        <span>{question}</span>
        <span className="ml-4 text-purple-400 text-lg">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div
          className="px-6 pb-4 text-sm text-gray-400 leading-relaxed"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {answer}
        </div>
      )}
    </div>
  );
}

export default function PricingPage() {
  usePageTitle("Pricing");
  const navigate = useNavigate();

  return (
    <div className="min-h-screen w-full bg-transparent text-white">
      <header className="border-b border-white/10 bg-slate-950/70 backdrop-blur sticky top-0 z-50">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-500 to-orange-400" />
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-wide">NeuroSphere</div>
              <div className="text-xs text-white/60">Voice • Brain • Memory</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              className="rounded-xl border border-white/15 px-4 py-2 text-sm text-white/80 hover:border-white/30 hover:text-white"
              onClick={() => navigate("/")}
            >
              Home
            </button>
            <button
              className="rounded-xl border border-white/15 px-4 py-2 text-sm text-white/80 hover:border-white/30 hover:text-white"
              onClick={() => navigate("/login")}
            >
              Login
            </button>
            <a
              className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-white/90"
              href={DEMO_BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Book a demo
            </a>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-16">
        <PageTitle>Pricing</PageTitle>
        <p className="text-center text-sm text-purple-300 mt-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          💡 A full-time receptionist costs $35,000–$50,000/year. NeuroSphere starts at ${(PLANS[0].priceMonthly * 12).toLocaleString()}/year.
        </p>
        <p className="mt-3 max-w-3xl text-white/70">
          {TRIAL.copy}
        </p>
        <p className="mt-2 text-sm text-green-400/80 italic">{TRIAL.roiLine}</p>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {PLANS.map((p) => (
            <div
              key={p.key}
              className={[
                "rounded-2xl border p-6",
                p.key === "growth"
                  ? "border-white/25 bg-white/10"
                  : "border-white/10 bg-white/5",
              ].join(" ")}
            >
              {p.key === "growth" && (
                <div className="mb-4 inline-flex rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-950">
                  Most popular
                </div>
              )}

              <div className="text-lg font-bold">{p.name}</div>

              <div className="mt-2 flex items-end gap-2">
                <div className="text-3xl font-extrabold">
                  {p.priceLabel ?? `From $${p.priceMonthly.toLocaleString()}`}
                </div>
                {!p.priceLabel && <div className="text-sm text-white/60">/mo</div>}
              </div>

              <p className="mt-1 text-sm text-white/80">{p.callVolume}</p>

              <p className="mt-3 text-sm text-white/70">{p.tagline}</p>

              <div className="mt-4 text-sm text-white/70 space-y-1">
                <div>
                  <span className="text-white/85 font-semibold">
                    Seats:
                  </span>{" "}
                  {p.seatsIncluded === "unlimited"
                    ? "Unlimited"
                    : p.seatsIncluded}
                </div>
              </div>

              <ul className="mt-5 space-y-2 text-sm text-white/75">
                {p.includes.slice(0, 6).map((b) => (
                  <li key={b} className="flex gap-2">
                    <i className="ri-checkbox-circle-fill text-green-400 mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6">
                <a
                  className={[
                    "inline-flex w-full items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold",
                    p.key === "growth"
                      ? "bg-white text-slate-950 hover:bg-white/90"
                      : "border border-white/15 text-white/90 hover:border-white/30",
                  ].join(" ")}
                  href={DEMO_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Book a demo
                </a>
                <div className="mt-2 text-xs text-white/55">
                  Set up with our team
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-white/10 bg-slate-950/40 p-6 text-sm text-white/70">
          <div className="font-semibold text-white/85">
            Enterprise / Carrier
          </div>
          <div className="mt-1">{ENTERPRISE.priceRange}</div>

          <div className="mt-4">
            <a
              className="inline-flex items-center justify-center rounded-xl border border-white/15 px-4 py-3 text-sm font-semibold text-white/90 hover:border-white/30"
              href="mailto:admin@neurospherevoiceai.com?subject=NeuroSphere%20Enterprise%20Demo"
            >
              Book a demo
            </a>
          </div>

          <ul className="mt-4 space-y-2">
            {ENTERPRISE.includes.slice(0, 6).map((x) => (
              <li key={x} className="flex gap-2">
                <i className="ri-checkbox-circle-fill text-green-400 mt-0.5 shrink-0" />
                <span>{x}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-10 rounded-2xl border border-white/10 bg-slate-950/40 p-6 text-sm text-white/70">
          <div className="font-semibold text-white/85">Optional Add-ons</div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {ADDONS.map((a) => (
              <div key={a.key} className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-white/85">{a.name}</div>
                  <div className="text-white/70">{a.price}</div>
                </div>
                <div className="mt-1 text-sm text-white/65">{a.description}</div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-sm text-gray-500 mt-8" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Plans include onboarding support.
        </p>

        <div className="mt-12 max-w-3xl mx-auto">
          <h2
            className="text-2xl font-bold text-white text-center mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Common questions
          </h2>
          <div className="space-y-4">
            {[
              {
                q: "Can I cancel anytime?",
                a: "Yes. Plans are month to month. Contact our team and we'll cancel with no penalty.",
              },
              {
                q: "How is pricing decided?",
                a: "Plans start at $299 a month. The final price depends on how many seats you need, the size of your business and which capabilities you want. We confirm it on your demo call.",
              },
              {
                q: "Do I need a tech team to set up?",
                a: "No. Setup takes about 5 minutes. You tell us your business name and how you want calls handled — your AI is live the same day.",
              },
            ].map(({ q, a }) => (
              <FaqItem key={q} question={q} answer={a} />
            ))}
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-gray-800 py-8 text-center">
        <div className="flex justify-center gap-6 mb-4 text-sm text-gray-500">
          <Link to="/">Home</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/login">Login</Link>
          <a href={DEMO_BOOKING_URL} target="_blank" rel="noopener noreferrer">Book a demo</a>
        </div>
        <p className="text-gray-500 text-sm mb-3" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
          © 2026 NeuroSphere. All rights reserved.
        </p>
        <div className="flex justify-center gap-6">
          <a href="#" className="text-gray-500 text-sm hover:text-gray-400" style={{ fontFamily: "Space Grotesk, sans-serif" }}>Privacy Policy</a>
          <a href="#" className="text-gray-500 text-sm hover:text-gray-400" style={{ fontFamily: "Space Grotesk, sans-serif" }}>Terms of Service</a>
        </div>
      </footer>
    </div>
  );
}
